import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Image } from 'react-bootstrap';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import FormContainer from '../../components/FormContainer';
import { toast } from 'react-toastify';
import {
  useGetProductDetailsQuery,
  useUploadProductImageMutation,
  useUpdateProductMutation,
} from '../../slices/productsApiSlice';
import moment from 'moment';
import { getImageUrl } from '../../utils/imageUtils';

const ProductEditScreen = () => {
  const { id: productId } = useParams();
  
  // États du formulaire
  const [name, setName] = useState('');
  const [type, setType] = useState(4); // Défaut à 4 (Général)
  const [proprio, setProprio] = useState('');
  const [price, setPrice] = useState(0);
  const [priceEur, setPriceEur] = useState(0);
  const [priceDol, setPriceDol] = useState(0);
  const [date, setDate] = useState('');
  const [position, setPosition] = useState(0);
  const [image, setImage] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [countInStock, setCountInStock] = useState(0);
  const [description, setDescription] = useState('');
  const [imagePreview, setImagePreview] = useState('');

  // Liste des catégories prédéfinies
  const categoriesList = [
    'Informatique',
    'Développement Personnel',
    // 'Développement Web',
    // 'Design Graphique',
    // 'Marketing Digital',
    // 'Langues',
    // 'Business',
    // 'Finance',
    // 'Santé',
    // 'Arts',
    // 'Musique',
    // 'Cuisine',
    // 'Sport',
    // 'Photographie',
    // 'Éducation',
    // 'Science',
    // 'Technologie',
    // 'Développement Personnel',
    // 'Management',
    // 'Communication',
    'Autre'
  ];

  // Types de produits - AJOUT du type "Général" (4)
  const productTypes = [
    { value: 4, label: 'Général (Par défaut)' },
    { value: 0, label: 'Formation Présentielle' },
    { value: 1, label: 'Formation en Ligne' },
    { value: 2, label: 'Autre Produit' }
  ];

  // Propriétaires
  const proprietairesList = [
    'formateur',
    'admin',
    'office',
    'vendeur'
  ];

  const {
    data: product,
    isLoading,
    refetch,
    error,
  } = useGetProductDetailsQuery(productId);

  const [uploadProductImage, { isLoading: loadingUpload }] =
    useUploadProductImageMutation();
  
  const [updateProduct, { isLoading: loadingUpdate }] =
    useUpdateProductMutation();
  
  const navigate = useNavigate();

  useEffect(() => {
    if (product) {
     // console.log('📥 PRODUIT CHARGÉ - type:', product.type, 'typeof:', typeof product.type);
      
      setName(product.name);
      setType(Number(product.type)); // Conversion explicite
      setProprio(product.proprio);
      setPrice(product.price);
      setPriceEur(product.priceEur);
      setPriceDol(product.priceDol);
      setDate(moment(product.date).format('YYYY-MM-DD'));
      setPosition(product.position);
      setImage(product.image);
      setBrand(product.brand);
      setCategory(product.category);
      setCountInStock(product.countInStock);
      setDescription(product.description);
      
      setImagePreview(getImageUrl(product.image));
    }
  }, [product]);

  const submitHandler = async (e) => {
    e.preventDefault();
    
   // console.log('🔍 AVANT ENVOI - type sélectionné:', type, 'typeof:', typeof type);

    try {
      // Préparer les données avec conversion explicite
      const updateData = {
        productId,
        name,
        type: Number(type), // Conversion garantie
        proprio,
        price: Number(price),
        priceEur: Number(priceEur),
        priceDol: Number(priceDol),
        date: moment(date).toDate(),
        image,
        brand,
        category,
        description,
        countInStock: Number(countInStock),
        position: Number(position),
      };

     // console.log('📤 DONNÉES ENVOYÉES:', updateData);

      // Utiliser la mutation standard
      await updateProduct(updateData).unwrap(); // Variable supprimée
      
     // console.log('✅ RÉPONSE BACKEND:', result);
      
      toast.success('Produit mis à jour avec succès');
      refetch();
      navigate('/admin/productlist');
    } catch (err) {
      console.error('❌ ERREUR:', err);
      toast.error(err?.data?.message || err.error);
    }
  };

  const uploadFileHandler = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    setImagePreview(objectUrl);

    const formData = new FormData();
    //correction
    formData.append('file', file);
    
    try {
      const res = await uploadProductImage(formData).unwrap();
      //correction
      toast.success(res.message);
      setImage(res.file.path);
      setImagePreview(getImageUrl(res.file.path));
    } catch (err) {
      toast.error(err?.data?.message || err.error);
      setImagePreview(getImageUrl(image));
    }
  };

  useEffect(() => {
    if (image && !image.startsWith('blob:')) {
      setImagePreview(getImageUrl(image));
    }
  }, [image]);

  return (
    <>
      <Link to='/admin/productlist' className='btn btn-light my-3'>
        ← Retour à la liste des produits
      </Link>
      <FormContainer>
        <h1>Modifier un produit</h1>
        {loadingUpdate && <Loader />}
        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant='danger'>{error}</Message>
        ) : (
          <Form onSubmit={submitHandler}>
            {/* ✅ Prévisualisation de l'image */}
            {imagePreview && (
              <div className="mb-3 text-center">
                <Image 
                  src={imagePreview} 
                  alt="Aperçu" 
                  fluid 
                  style={{ maxHeight: '200px', objectFit: 'cover' }}
                  onError={() => setImagePreview('/images/placeholder.jpg')}
                />
                <div className="text-muted small mt-2">
                  Aperçu de l'image
                </div>
              </div>
            )}

            {/* Nom du produit */}
            <Form.Group controlId='name' className='my-2'>
              <Form.Label>Nom du produit</Form.Label>
              <Form.Control
                type='text'
                placeholder='Entrer le nom du produit'
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>

            {/* Type de produit - Liste déroulante AVEC GÉNÉRAL */}
            <Form.Group controlId='type' className='my-2'>
              <Form.Label>Type de produit</Form.Label>
              <Form.Select
                value={type}
                onChange={(e) => setType(Number(e.target.value))}
              >
                {productTypes.map((typeOption) => (
                  <option key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {type === 4 && "Produit général - Type par défaut"}
                {type === 0 && "Formation en présentiel"}
                {type === 1 && "Formation en ligne (cours vidéo)"}
                {type === 2 && "Autre type de produit ou service"}
              </Form.Text>
            </Form.Group>

            {/* Propriétaire - Liste déroulante */}
            <Form.Group controlId='proprio' className='my-2'>
              <Form.Label>Propriétaire</Form.Label>
              <Form.Select
                value={proprio}
                onChange={(e) => setProprio(e.target.value)}
              >
                <option value="">Sélectionner un propriétaire</option>
                {proprietairesList.map((prop) => (
                  <option key={prop} value={prop}>
                    {prop.charAt(0).toUpperCase() + prop.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Prix en TND */}
            <Form.Group controlId='price' className='my-2'>
              <Form.Label>Prix en TND</Form.Label>
              <Form.Control
                type='number'
                placeholder='Entrer le prix en dinars tunisiens'
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                min="0"
                step="0.01"
              />
            </Form.Group>

            {/* Prix en EUR */}
            <Form.Group controlId='priceEur' className='my-2'>
              <Form.Label>Prix en EUR</Form.Label>
              <Form.Control
                type='number'
                placeholder='Entrer le prix en euros'
                value={priceEur}
                onChange={(e) => setPriceEur(e.target.value)}
                min="0"
                step="0.01"
              />
            </Form.Group>

            {/* Prix en USD */}
            <Form.Group controlId='priceDol' className='my-2'>
              <Form.Label>Prix en USD</Form.Label>
              <Form.Control
                type='number'
                placeholder='Entrer le prix en dollars'
                value={priceDol}
                onChange={(e) => setPriceDol(e.target.value)}
                min="0"
                step="0.01"
              />
            </Form.Group>

            {/* Image */}
            <Form.Group controlId='image' className='my-2'>
              <Form.Label>Image du produit</Form.Label>
              <Form.Control
                type='text'
                placeholder="Entrer le lien de l'image"
                value={image}
                onChange={(e) => setImage(e.target.value)}
              />
              <Form.Label className='mt-2'>Ou télécharger une image :</Form.Label>
              <Form.Control
                onChange={uploadFileHandler}
                type='file'
                accept="image/*"
              />
              {loadingUpload && <Loader />}
            </Form.Group>

            {/* Marque */}
            <Form.Group controlId='brand' className='my-2'>
              <Form.Label>Marque / Éditeur</Form.Label>
              <Form.Control
                type='text'
                placeholder="Entrer la marque ou l'éditeur"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              />
            </Form.Group>

            {/* Catégorie - Liste déroulante */}
            <Form.Group controlId='category' className='my-2'>
              <Form.Label>Catégorie</Form.Label>
              <Form.Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Sélectionner une catégorie</option>
                {categoriesList.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                La catégorie permet de filtrer les produits dans la recherche.
              </Form.Text>
            </Form.Group>

            {/* Stock */}
            <Form.Group controlId='countInStock' className='my-2'>
              <Form.Label>Quantité en stock</Form.Label>
              <Form.Control
                type='number'
                placeholder='Entrer la quantité disponible'
                value={countInStock}
                onChange={(e) => setCountInStock(e.target.value)}
                min="0"
              />
            </Form.Group>

            {/* Description */}
            <Form.Group controlId='description' className='my-2'>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder='Décrire le produit en détail...'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>

            {/* Date */}
            <Form.Group controlId='date' className='my-2'>
              <Form.Label>Date de création</Form.Label>
              <Form.Control
                type='date'
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Form.Group>

            {/* Position */}
            <Form.Group controlId='position' className='my-2'>
              <Form.Label>Position d'affichage</Form.Label>
              <Form.Control
                type='number'
                placeholder='Position dans la liste (0 = premier)'
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                min="0"
              />
              <Form.Text className="text-muted">
                Les produits sont triés par position croissante.
              </Form.Text>
            </Form.Group>

            {/* Bouton de soumission */}
            <div className="d-grid gap-2">
              <Button
                type='submit'
                variant='primary'
                size='lg'
                className='my-3'
                disabled={loadingUpdate}
              >
                {loadingUpdate ? 'Mise à jour...' : 'Mettre à jour le produit'}
              </Button>
            </div>
          </Form>
        )}
      </FormContainer>
    </>
  );
};

export default ProductEditScreen;